import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CalendarBlank,
  Copy,
  DotsThree,
  DownloadSimple,
  LinkSimple,
  MagnifyingGlass,
  Plus,
  QrCode,
  TrendUp,
  Users,
  WhatsappLogo,
  ShoppingBag,
  CurrencyDollar,
  Eye,
  PencilSimple,
} from '@phosphor-icons/react'
import merchantApi from '../../services/merchantApi'
import {
  destinationToApi,
  downloadQrCode,
  formatDateTime,
  formatMoney,
  formatNumber,
  getApiList,
  mapCampaignFromApi,
  sourceToApi,
  statusToApi,
} from './campaignLinkUtils'
import './CampaignLinksPage.scss'
import { Grid } from "@mui/material";
import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import DateRangeDropdown from '../../components/DateRangeDropdown/DateRangeDropdown'

const SOURCETYPES = [
  { label: 'Website', value: 'Website' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Instagram', value: 'Instagram' },
  { label: 'TikTok', value: 'TikTok' },
  { label: 'X (Twitter)', value: 'X (Twitter)' },
  { label: 'WhatsApp', value: 'WhatsApp' },
  { label: 'QR Code', value: 'QR Code' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Influencer', value: 'Influencer' },
  { label: 'Others', value: 'Others' },
]

const DESTINATIONTYPES = [
  { label: 'WhatsApp Chat', value: 'WhatsApp Chat' },
  { label: 'Mini Program Home', value: 'Mini Program Home' },
  { label: 'Product Page', value: 'Product Page' },
  { label: 'Promotion Page', value: 'Promotion Page' },
  { label: 'Voucher Page', value: 'Voucher Page' },
]

const STATUSTYPES = [
  { label: 'Active', value: 'Active' },
  { label: 'Paused', value: 'Paused' },
  { label: 'Draft', value: 'Draft' },
]

function KpiCard({ icon, label, value, trend }) {
  return (
    <div className="campaign-kpi-card">
      <div className="campaign-kpi-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{trend ? `↑ ${trend} vs last 7 days` : 'Live from campaign links'}</small>
      </div>
    </div>
  )
}

export default function CampaignLinksPage() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState([])
  const [campaignsMeta, setCampaignsMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [status, setStatus] = useState('')
  const [period, setPeriod] = useState('')
  const [customDateRange, setCustomDateRange] = useState('')
  const [activeMenu, setActiveMenu] = useState(null)
  const tableMenuRef = useRef(null)

  const loadCampaigns = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await merchantApi.campaignLinks.list({
        page: currentPage,
        keyword: search,
        status: status === 'All Status' ? '' : statusToApi(status),
        traffic_source: source === 'All Sources' ? '' : sourceToApi(source),
        destination_type: destination === 'All Destinations' ? '' : destinationToApi(destination),
      })
      setCampaigns(getApiList(response).map(mapCampaignFromApi))
      setCampaignsMeta({
        total: response?.data?.total || 0,
        from: response?.data?.from || 0,
        to: response?.data?.to || 0,
        last_page: response?.data?.last_page || 1,
        current_page: response?.data?.current_page || currentPage,
        per_page: response?.data?.per_page || 20,
      })
    } catch (err) {
      setError(err.message || 'Unable to load campaign links.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(loadCampaigns, 250)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, source, destination, status, currentPage])

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!tableMenuRef.current) return
      if (!tableMenuRef.current.contains(event.target)) setActiveMenu(null)
    }

    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [])

  const openRowMenu = (event, campaignId) => {
    event.stopPropagation()
    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 180
    const left = Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth))
    const top = Math.min(window.innerHeight - 120, rect.bottom + 8)

    setActiveMenu((current) => current?.id === campaignId ? null : {
      id: campaignId,
      top,
      left,
    })
  }

  const totals = useMemo(() => campaigns.reduce((acc, campaign) => ({
    clicks: acc.clicks + Number(campaign.clicks || 0),
    visitors: acc.visitors + Number(campaign.visitors || 0),
    waOpens: acc.waOpens + Number(campaign.waOpens || 0),
    orders: acc.orders + Number(campaign.orders || 0),
    revenue: acc.revenue + Number(campaign.revenue || 0),
  }), { clicks: 0, visitors: 0, waOpens: 0, orders: 0, revenue: 0 }), [campaigns])

  const handleDownloadQr = async (campaign) => {
    await downloadQrCode(campaign.smartLink, `${campaign.slug || campaign.name || 'campaign-link'}-qr.png`)
    setActiveMenu(null)
  }

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      alert('Copied to clipboard.')
    } catch (_) {
      alert(value)
    }
  }

  const columns = [
    {
      field: 'campaign_name',
      header: 'Campaign Name',
      minWidth: 180,
      render: (campaign) => (
        <>
          <Link to={`/marketing/links/${campaign.id}`} className="campaign-name">{campaign.name}</Link>
          <small>{campaign.description}</small>
        </>
      ),
    },
    {
      field: 'smart_link',
      header: 'Smart Link',
      minWidth: 220,
      render: (campaign) => (
        <div className="campaign-link-line">
          <span>{campaign.smartLink}</span>
          <button type="button" onClick={(event) => {
            event.stopPropagation()
            copyText(campaign.smartLink)
          }}>
            <Copy size={15} />
          </button>
        </div>
      ),
    },
    {
      field: 'destination',
      header: 'Destination',
      minWidth: 170,
      render: (campaign) => (
        <>
          <strong>{campaign.destination}</strong>
          <small>{campaign.destinationMeta}</small>
        </>
      ),
    },
    {
      field: 'source',
      header: 'Source',
      minWidth: 160,
      render: (campaign) => (
        <>
          <strong>{campaign.source}</strong>
          <small>{campaign.medium}</small>
        </>
      ),
    },
    { field: 'clicks', header: 'Clicks', minWidth: 90, render: (campaign) => formatNumber(campaign.clicks) },
    { field: 'visitors', header: 'Visitors', minWidth: 90, render: (campaign) => formatNumber(campaign.visitors) },
    { field: 'wa_opens', header: 'WA Opens', minWidth: 100, render: (campaign) => formatNumber(campaign.waOpens) },
    { field: 'orders', header: 'Orders', minWidth: 90, render: (campaign) => formatNumber(campaign.orders) },
    { field: 'revenue', header: 'Revenue', minWidth: 110, render: (campaign) => formatMoney(campaign.revenue) },
    {
      field: 'status',
      header: 'Status',
      minWidth: 110,
      render: (campaign) => (
        <span className={`campaign-status campaign-status-${campaign.status.toLowerCase()}`}>{campaign.status}</span>
      ),
    },
    {
      field: 'created_at',
      header: 'Created At',
      minWidth: 170,
      render: (campaign) => formatDateTime(campaign.createdAt),
    },
    {
      field: 'actions',
      header: 'Actions',
      minWidth: 100,
      render: (campaign) => (
        <>
          <button
            className="campaign-icon-btn campaign-row-menu-trigger"
            type="button"
            title="More actions"
            onClick={(event) => {
              event.stopPropagation()
              openRowMenu(event, campaign.id)
            }}
          >
            <DotsThree size={22} />
          </button>
          {activeMenu?.id === campaign.id && (
            <div
              ref={tableMenuRef}
              className="campaign-row-menu campaign-row-menu-fixed"
              style={{ top: `${activeMenu.top}px`, left: `${activeMenu.left}px` }}
              onClick={(event) => event.stopPropagation()}
            >
              <button type="button" onClick={() => { setActiveMenu(null); navigate(`/marketing/links/${campaign.id}`) }}><Eye size={16} /> View</button>
              <button type="button" onClick={() => { setActiveMenu(null); navigate(`/marketing/links/${campaign.id}/edit`) }}><PencilSimple size={16} /> Edit</button>
              <button type="button" onClick={() => { copyText(campaign.smartLink); setActiveMenu(null) }}><Copy size={16} /> Copy Link</button>
              <button type="button" onClick={() => handleDownloadQr(campaign)}><QrCode size={16} /> Download QR</button>
            </div>
          )}
        </>
      ),
    },
  ]



  return (
    <div className="">
      <Header
        items={[
          { label: 'Marketing', to: '/' },
          { label: 'Campaign Links', current: true },
        ]}
      />
      <div className="campaign-page-header">
        <div className="campaign-header-title">
          <h1>Campaign Links</h1>
          <p>Create, manage and track your smart links across all channels.</p>
        </div>
        <div className="campaign-header-actions">
          <button className="campaign-btn campaign-btn-light" type="button"><DownloadSimple size={18} /> Export</button>
          <button className="campaign-btn campaign-btn-primary" type="button" onClick={() => navigate('/marketing/links/create')}><Plus size={18} /> Create New Link</button>
        </div>
      </div>
      <Grid container spacing={2} mb={2}>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<TrendUp size={28} />} label="Total Clicks" value={formatNumber(totals.clicks)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Users size={28} />} label="Unique Visitors" value={formatNumber(totals.visitors)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<WhatsappLogo size={28} />} label="WhatsApp Opens" value={formatNumber(totals.waOpens)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<ShoppingBag size={28} />} label="Orders" value={formatNumber(totals.orders)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<CurrencyDollar size={28} />} label="Revenue" value={formatMoney(totals.revenue)} trend="" />
        </Grid>
      </Grid>

      <div className="campaign-filter-row">
        <div className="campaign-filter-row__filters">
          <FilterDropdown
            label="Source"
            value={source}
            options={SOURCETYPES}
            onChange={(value) => setSource(value)}
          />
          <FilterDropdown
            label="Destination"
            value={destination}
            options={DESTINATIONTYPES}
            onChange={(value) => setDestination(value)}
          />
          <FilterDropdown
            label="Status"
            value={status}
            options={STATUSTYPES}
            onChange={(value) => setStatus(value)}
          />
          <DateRangeDropdown
            value={period}
            customRange={customDateRange}
            onChange={(value) => setPeriod(value)}
            onCustomRange={(value) => setCustomDateRange(value)}
          />
        </div>

        {/* <select value={source} onChange={(event) => { setCurrentPage(1); setSource(event.target.value) }}>
          <option>All Sources</option><option>Website</option><option>Facebook</option><option>Instagram</option><option>TikTok</option><option>X (Twitter)</option><option>WhatsApp</option><option>QR Code</option><option>Email</option><option>SMS</option><option>Influencer</option><option>Others</option>
        </select>
        <select value={destination} onChange={(event) => { setCurrentPage(1); setDestination(event.target.value) }}>
          <option>All Destinations</option><option>WhatsApp Chat</option><option>Mini Program Home</option><option>Product Page</option><option>Promotion Page</option><option>Voucher Page</option>
        </select>
        <select value={status} onChange={(event) => { setCurrentPage(1); setStatus(event.target.value) }}>
          <option>All Status</option><option>Active</option><option>Paused</option><option>Draft</option>
        </select> 
        <button className="campaign-date" type="button"><CalendarBlank size={18} /> Current period</button>
        */}
        <div className="campaign-search">
          <MagnifyingGlass size={18} />
          <input value={search} onChange={(event) => { setCurrentPage(1); setSearch(event.target.value) }} placeholder="Search campaign name or link..." />
        </div>
      </div>

      <div className="campaign-table-card">
        {error && <div className="campaign-error">{error}</div>}
        <div className="campaign-table-wrap">
          <AppTable
            columns={columns}
            rows={campaigns}
            pagination={campaignsMeta}
            loading={loading}
            emptyText="No campaign links found."
            onRowClick={(row) => navigate(`/marketing/links/${row.id}`)}
            onPageChange={(page) => {
              setCurrentPage(page)
            }}
          />
        </div>
      </div>

      <div className="campaign-help-card">
        <div><LinkSimple size={24} /><strong>Create Link</strong><span>Set destination and tracking source.</span></div>
        <div><QrCode size={24} /><strong>Share Anywhere</strong><span>Social, ads, QR or email.</span></div>
        <div><TrendUp size={24} /><strong>Track Performance</strong><span>Clicks to revenue.</span></div>
      </div>
    </div>
  )
}
